/**
 * ITU-T E.164 country dial codes — keyed by ISO 3166-1 alpha-2.
 *
 * Parallel to `lib/countries.ts` (which owns display names + flag URL).
 * Kept as a separate module on purpose: the residence-address picker
 * cares about "country name", the phone picker cares about "dial code"
 * — different concerns, even when the keys match.
 *
 * Values are dial codes WITHOUT the leading "+" so callers compose
 * their own display ("+" + dial). Stored as strings so that codes
 * like "1242" (Bahamas, NANP) and "1" (US/CA) coexist; the matching
 * logic always treats the value as opaque text.
 *
 * Coverage: every UN member state + the dependencies referenced by
 * `lib/countries.ts`. Add a row when you add a country there.
 */

export const DIAL_BY_ISO2: Readonly<Record<string, string>> = {
  // EU + EEA + UK + CH
  IT: '39',
  AT: '43',
  BE: '32',
  BG: '359',
  HR: '385',
  CY: '357',
  CZ: '420',
  DK: '45',
  EE: '372',
  FI: '358',
  FR: '33',
  DE: '49',
  GR: '30',
  HU: '36',
  IE: '353',
  LV: '371',
  LT: '370',
  LU: '352',
  MT: '356',
  NL: '31',
  PL: '48',
  PT: '351',
  RO: '40',
  SK: '421',
  SI: '386',
  ES: '34',
  SE: '46',
  IS: '354',
  LI: '423',
  NO: '47',
  CH: '41',
  GB: '44',
  // Italian microstates / overseas / dependencies
  SM: '378',
  VA: '39', // Vatican City uses Italy's prefix in practice
  MC: '377',
  AD: '376',
  GI: '350',
  IM: '44',
  JE: '44',
  GG: '44',
  AX: '358',
  FO: '298',
  // Balkans + Eastern Europe
  AL: '355',
  BA: '387',
  ME: '382',
  MK: '389',
  RS: '381',
  XK: '383',
  TR: '90',
  RU: '7',
  BY: '375',
  UA: '380',
  MD: '373',
  // Caucasus + Central Asia
  AM: '374',
  AZ: '994',
  GE: '995',
  KZ: '7',
  KG: '996',
  TJ: '992',
  TM: '993',
  UZ: '998',
  // Middle East
  IL: '972',
  PS: '970',
  JO: '962',
  LB: '961',
  SY: '963',
  IQ: '964',
  IR: '98',
  SA: '966',
  YE: '967',
  OM: '968',
  AE: '971',
  QA: '974',
  BH: '973',
  KW: '965',
  AF: '93',
  // South Asia
  PK: '92',
  IN: '91',
  LK: '94',
  BD: '880',
  BT: '975',
  NP: '977',
  MV: '960',
  // South-East + East Asia
  MM: '95',
  TH: '66',
  LA: '856',
  KH: '855',
  VN: '84',
  PH: '63',
  ID: '62',
  MY: '60',
  SG: '65',
  BN: '673',
  TL: '670',
  TW: '886',
  KP: '850',
  KR: '82',
  JP: '81',
  CN: '86',
  HK: '852',
  MO: '853',
  MN: '976',
  // North America (NANP — all share +1)
  US: '1',
  CA: '1',
  BS: '1',
  BB: '1',
  AG: '1',
  AI: '1',
  BM: '1',
  KY: '1',
  DM: '1',
  GD: '1',
  JM: '1',
  MS: '1',
  PR: '1',
  KN: '1',
  LC: '1',
  VC: '1',
  TT: '1',
  TC: '1',
  VG: '1',
  VI: '1',
  AS: '1',
  GU: '1',
  MP: '1',
  SX: '1',
  // Latin America
  MX: '52',
  BZ: '501',
  CR: '506',
  SV: '503',
  GT: '502',
  HN: '504',
  NI: '505',
  PA: '507',
  CU: '53',
  HT: '509',
  DO: '1',
  AR: '54',
  BO: '591',
  BR: '55',
  CL: '56',
  CO: '57',
  EC: '593',
  PE: '51',
  PY: '595',
  UY: '598',
  VE: '58',
  GY: '592',
  SR: '597',
  AW: '297',
  CW: '599',
  // French overseas
  GF: '594',
  GP: '590',
  MQ: '596',
  YT: '262',
  RE: '262',
  BL: '590',
  MF: '590',
  PM: '508',
  NC: '687',
  PF: '689',
  WF: '681',
  // Africa
  DZ: '213',
  EG: '20',
  LY: '218',
  MA: '212',
  EH: '212',
  TN: '216',
  SD: '249',
  SS: '211',
  ER: '291',
  ET: '251',
  DJ: '253',
  SO: '252',
  KE: '254',
  TZ: '255',
  UG: '256',
  RW: '250',
  BI: '257',
  CD: '243',
  CG: '242',
  CF: '236',
  GA: '241',
  GQ: '240',
  CM: '237',
  NG: '234',
  GH: '233',
  CI: '225',
  SN: '221',
  ML: '223',
  BF: '226',
  NE: '227',
  TG: '228',
  BJ: '229',
  LR: '231',
  SL: '232',
  GN: '224',
  GW: '245',
  GM: '220',
  CV: '238',
  MR: '222',
  AO: '244',
  ZM: '260',
  ZW: '263',
  BW: '267',
  NA: '264',
  ZA: '27',
  SZ: '268',
  LS: '266',
  MZ: '258',
  MW: '265',
  MG: '261',
  KM: '269',
  MU: '230',
  SC: '248',
  ST: '239',
  SH: '290',
  TD: '235',
  // Oceania
  AU: '61',
  NZ: '64',
  FJ: '679',
  PG: '675',
  SB: '677',
  VU: '678',
  WS: '685',
  TO: '676',
  KI: '686',
  NR: '674',
  PW: '680',
  FM: '691',
  MH: '692',
  TV: '688',
  CK: '682',
  NU: '683',
  TK: '690',
  // Misc territories (Italian docs may reference)
  GL: '299',
  CX: '61',
  CC: '61',
  NF: '672',
  PN: '64',
  FK: '500',
};

/** Get the dial code for a given ISO2 (e.g. `'IT'` → `'39'`). */
export function getDialCode(iso2: string | null | undefined): string | undefined {
  if (!iso2) return undefined;
  return DIAL_BY_ISO2[iso2.toUpperCase()];
}
